/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { i18n } from '@/plugins/i18n';
import ActivationCodeApi from '@/services/licensing/ActivationCodeApi';
import LicenseApi from '@/services/licensing/LicenseApi';
import { defineStore } from 'pinia';
import {
  AddActivationCode,
  License,
  LicenseStatus,
  LicenseType
} from '../../models/licenses/licenseTypes';
import {
  showApiErrorSnackbar,
  createErrorMessage,
  showSnackbar
} from '../../util/snackBarUtil';
import { authenticationStore } from './AuthenticationStore';

interface State {
  licenses: License[];
  activationCodes: License[];
}

const getLeftDaysOfLicense = (license: License): number => {
  if (!license) return 0;
  if (!license.expiresAt) return Number.POSITIVE_INFINITY;
  const expires = new Date(license.expiresAt);
  const now = new Date();
  const leftDays = Math.floor(
    (expires.getTime() - now.getTime()) / (1000 * 3600 * 24)
  );
  return leftDays;
};

const compareLicensesByExpiry = (first: License, second: License): number => {
  if (!first.expiresAt) {
    return !second.expiresAt ? first.uuid.localeCompare(second.uuid) : 1;
  }
  if (!second.expiresAt) return -1;

  const difference =
    new Date(first.expiresAt).getTime() - new Date(second.expiresAt).getTime();
  return difference || first.uuid.localeCompare(second.uuid);
};

export const licenseStore = defineStore('licenseStore', {
  state: (): State => ({
    licenses: [] as License[],
    activationCodes: [] as License[]
  }),

  getters: {
    // trial account can only have 1 active license
    trialIsSoonExpiring: (state) =>
      getLeftDaysOfLicense(state.licenses[0]) <= 14,
    getLicensesAndActivationCodes: (state) => {
      const licensesAndActivationCodes = state.licenses
        ?.concat(state.activationCodes ?? [])
        .filter(
          (activationCode) => (activationCode.status as string) !== 'EXHAUSTED'
        );
      return licensesAndActivationCodes;
    },
    getActiveLicenses: (state) =>
      state.licenses
        .filter(
          (license: License) =>
            license.status === LicenseStatus.LIVE &&
            license.type === LicenseType.LICENSE
        )
        .sort(compareLicensesByExpiry),
    leftDaysOfTrialLicense: (state) =>
      getLeftDaysOfLicense(state.licenses[0]).toString(),
    getTotalDeviceCount(): number {
      const isInternal = authenticationStore().isInternal;
      if (isInternal) return Number.MAX_VALUE;
      return this.getActiveLicenses.reduce(
        (acc, license) => acc + (license.deviceCount ?? 0),
        0
      );
    },
    soonExpiringLicenses(): License[] {
      return this.getActiveLicenses.filter((license) => {
        const leftDays = getLeftDaysOfLicense(license);
        if (leftDays < 46) {
          return license;
        }
      });
    }
  },

  actions: {
    async fetchLicenses(errorHandling = true) {
      try {
        this.licenses = (await LicenseApi.find()).data.members as License[];
      } catch (error) {
        if (errorHandling) {
          showApiErrorSnackbar(createErrorMessage(error), error);
        }
        throw error;
      }
    },
    async fetchActivationCodes(errorHandling = true) {
      try {
        this.activationCodes = (await ActivationCodeApi.find()).data
          .members as License[];
      } catch (error) {
        if (errorHandling) {
          showApiErrorSnackbar(createErrorMessage(error), error);
        }
        throw error;
      }
    },
    async fetchLicensesAndActivationCodes(errorHandling = true) {
      try {
        await Promise.all([
          this.fetchLicenses(false),
          this.fetchActivationCodes(false)
        ]);
      } catch (error) {
        if (errorHandling) {
          showApiErrorSnackbar(createErrorMessage(error), error);
        }
        throw error;
      }
    },

    async addActivationCode(data: AddActivationCode, errorHandling = true) {
      try {
        await ActivationCodeApi.add(data);
        await this.fetchLicensesAndActivationCodes();
        showSnackbar(i18n.t('settings.addActivationCodeSuccess').toString());
      } catch (error) {
        if (errorHandling) {
          showApiErrorSnackbar(createErrorMessage(error), error);
        }
        throw error;
      }
    },

    async activateSingleActivationCode(
      code: string,
      startTime: string,
      errorHandling = true
    ) {
      try {
        const { data } = await LicenseApi.generate(code, startTime);
        await LicenseApi.activate(data.uuid, startTime);
      } catch (error) {
        if (errorHandling) {
          showApiErrorSnackbar(createErrorMessage(error), error);
        }
        throw error;
      }
    }
  }
});
